import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Status =
  | "loading"
  | "needs_onboarding"
  | "in_progress"
  | "completed"
  | "no_session";

// Helper to select display role from multiple roles
// Priority: employer > job_seeker (super_admin is a permission, not a display role)
function selectDisplayRole(roles: string[]): 'employer' | 'job_seeker' | null {
  if (roles.includes('employer')) return 'employer';
  if (roles.includes('job_seeker')) return 'job_seeker';
  return null;
}

/**
 * Fetches onboarding status for a given user ID.
 * Now accepts userId directly to enable parallel fetching with auth.
 */
export function useOnboardingStatus(userId: string | null | undefined) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["onboarding-status", userId],
    queryFn: async () => {
      const start = performance.now();
      
      if (!userId) {
        return {
          status: "no_session" as Status,
          role: null,
          userId: null,
        };
      }

      const uid = userId;

      // Fetch all roles and progress in parallel
      const [rolesRes, progressRes] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", uid),
        supabase.from("onboarding_progress")
          .select("current_step, completed_at")
          .eq("user_id", uid)
          .maybeSingle(),
      ]);
      
      const duration = performance.now() - start;
      if (duration > 500) {
        console.warn(`[Perf] Onboarding status check took ${duration.toFixed(0)}ms`);
      }

      if (rolesRes.error) throw rolesRes.error;
      if (progressRes.error) throw progressRes.error;

      const roles = rolesRes.data?.map(r => r.role) || [];
      const role = selectDisplayRole(roles);
      const progress = progressRes.data;
      
      let status: Status;
      if (!progress) {
        status = "needs_onboarding";
      } else if (progress.completed_at) {
        status = "completed";
      } else {
        status = "in_progress";
      }

      return { status, role, userId: uid };
    },
    enabled: !!userId,
    staleTime: 30 * 1000,           // 30 seconds - align with global settings
    gcTime: 5 * 60 * 1000,          // 5 minutes cache retention
    refetchOnWindowFocus: true,     // Refetch on tab switch for fresh state
    refetchOnMount: true,           // Always check on mount
  });

  return {
    loading: isLoading,
    status: data?.status ?? "loading",
    role: data?.role ?? null,
    userId: data?.userId ?? null,
    error: error?.message ?? null,
  };
}

// Re-import for the wrapper function
import { useAuthSession } from "./useAuthSession";

/**
 * Legacy wrapper that auto-fetches userId from session.
 * Use useOnboardingStatus(userId) directly in ProtectedRoute for parallel fetching.
 */
export function useOnboardingStatusWithSession() {
  const { data: session } = useAuthSession();
  return useOnboardingStatus(session?.user?.id);
}
